import { useEffect, useState } from "react";
import { Box, Card, CardContent, Typography, Button, Stack, Skeleton, Chip } from "@mui/material";
import { CalendarMonth as CalendarIcon, Assignment as AssignmentIcon, Schedule as ScheduleIcon } from "@mui/icons-material";
import { gateway } from "../../../gateway";
import { BaseModalComponent } from "../../common/BaseModalComponent";
import { JobsCalendar } from "../../jobs2/jobs-calendar/JobsCalendar";
import { WorkforceTaskCard } from "./WorkforceTaskCard";
import { colors } from "../../ui/colors";
import { WorkforceTasksFilter } from "./WorkforceTasksFilter";

const TaskSkeleton = () => (
  <Card elevation={0} sx={{ mb: 2.5, borderRadius: 3, border: `1px solid ${colors.gray[200]}` }}>
    <CardContent sx={{ p: 3 }}>
      <Stack spacing={2}>
        <Skeleton variant="rectangular" width="60%" height={24} sx={{ borderRadius: 1 }} />
        <Skeleton variant="rectangular" width="100%" height={20} sx={{ borderRadius: 1 }} />
        <Skeleton variant="rectangular" width="80%" height={20} sx={{ borderRadius: 1 }} />
        <Skeleton variant="rectangular" width="70%" height={20} sx={{ borderRadius: 1 }} />
      </Stack>
    </CardContent>
  </Card>
);

const EmptyState = () => (
  <Card elevation={0} sx={{ borderRadius: 3, border: `1px solid ${colors.gray[200]}` }}>
    <CardContent sx={{ p: 6, textAlign: "center" }}>
      <Box
        sx={{
          width: 80,
          height: 80,
          borderRadius: "50%",
          bgcolor: colors.gray[100],
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          mx: "auto",
          mb: 3,
        }}
      >
        <ScheduleIcon sx={{ fontSize: 32, color: colors.gray[400] }} />
      </Box>
      <Typography variant="h6" sx={{ color: colors.gray[700], fontWeight: 600, mb: 1 }}>
        No assigned tasks
      </Typography>
      <Typography variant="body2" sx={{ color: colors.gray[500], maxWidth: 300, mx: "auto", lineHeight: 1.6 }}>
        When tasks are assigned to this workforce member, they will appear here
      </Typography>
    </CardContent>
  </Card>
);

export const WorkforceTasks = ({ workforce }) => {
  const [assignedTasks, setAssignedTasks] = useState(null);
  const [filter, setFilter] = useState(null);

  const handleFilterSelect = (filter) => {
    setFilter(filter);
    console.log(filter, "was selected");
  }

  useEffect(() => {
    const fetchAssignedTasks = async () => {
      try {
        const token = sessionStorage.getItem("token");
        const response = await gateway.get(`/jobs/self/workforce/${workforce.uid}?status=assigned`, {
          headers: { Authorization: `${token}` },
        });
        setAssignedTasks(response.data.data.items);
      } catch (error) {
        console.error("Error fetching tasks:", error);
      }
    };

    fetchAssignedTasks();
  }, [workforce.uid]);

  const isLoading = assignedTasks === null;
  const workforceLabel = `${workforce?.firstName ?? ""} ${workforce?.lastName ?? ""} - ${workforce?.workforceRole ?? workforce?.type ?? ""}`.trim();
  const calendarLabel =
    `Jobs Calendar - ${workforce?.workforceRole ?? workforce?.type ?? ""} - ${workforce?.firstName ?? ""} ${workforce?.lastName ?? ""}`.trim();

  const filteredTasks = !filter || filter === "all" ? assignedTasks : assignedTasks?.filter((task) => task.status === filter);

  return (
    <Box sx={{ maxWidth: 1200, mx: "auto" }}>
      <Card elevation={0} sx={{ borderRadius: 3, mb: 3 }}>
        <CardContent>
          <Box display="flex" alignItems="center" gap={2} mb={3}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: 2,
                bgcolor: colors.orange[50],
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                border: `1px solid ${colors.orange[200]}`,
              }}
            >
              <AssignmentIcon sx={{ fontSize: 24, color: colors.orange.base }} />
            </Box>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700, color: colors.gray[900] }}>
                Jobs Management
              </Typography>
              <Typography variant="body2" sx={{ color: colors.gray[500], mt: 0.5 }}>
                {workforceLabel}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", flexDirection: "row", gap: 2, justifyContent: "space-between", px: 0 }}>
            <Box>
              <WorkforceTasksFilter status={filter} amount={assignedTasks?.length ?? 0} onSelectFilter={handleFilterSelect} />
            </Box>
          </Box>
        </CardContent>
      </Card>

      <Box sx={{ mb: 3, px: 3 }}>
        {isLoading ? (
          Array.from({ length: 3 }).map((_, i) => <TaskSkeleton key={i} />)
        ) : filteredTasks.length > 0 ? (
          <Stack spacing={2}>
            {filteredTasks.map((job) => (
              <WorkforceTaskCard key={job?.id} job={job} />
            ))}
          </Stack>
        ) : (
          <EmptyState />
        )}
      </Box>

      <Card elevation={0} sx={{ borderRadius: 3 }}>
        <CardContent sx={{ p: 3, textAlign: "center" }}>
          <BaseModalComponent
            modalKey="JobsCalendar-Business-Workforce"
            modalLabel={calendarLabel}
            elementTrigger={
              <Button
                variant="contained"
                startIcon={<CalendarIcon />}
                size="large"
                sx={{
                  bgcolor: colors.orange.base,
                  color: "white",
                  fontWeight: 600,
                  fontSize: "0.95rem",
                  textTransform: "none",
                  borderRadius: 2,
                  px: 4,
                  py: 1.5,
                  boxShadow: `0 4px 12px ${colors.orange[200]}`,
                  transition: "all 0.2s ease",
                  "&:hover": {
                    bgcolor: colors.orange[600],
                    transform: "translateY(-1px)",
                    boxShadow: `0 6px 20px ${colors.orange[300]}`,
                  },
                }}
              >
                View Calendar
              </Button>
            }
            sx={{ width: "100%" }}
          >
            <JobsCalendar userUid={workforce.uid} />
          </BaseModalComponent>
        </CardContent>
      </Card>
    </Box >
  );
};